import { Box, Skeleton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LinkButton } from '@/components/form/baseInputs/Link/LinkButton';
import { OnboardingModal } from '@/components/modals/OnboardingModal/OnboardingModal';
import {
  OnboardingSidebarButton,
  OnboardingTitle,
} from '@/components/modals/OnboardingModal/OnboardingModal.components';
import {
  StepTracker,
  StepTrackerStatus,
} from '@/components/navigation/StepTracker/StepTracker';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  ClientOnboardingAnalysisCard,
  ClientOnboardingBalanceSheetCard,
  ClientOnboardingDocumentSummaryCard,
  ClientOnboardingEntityCard,
  ClientOnboardingEstateSnapshotCard,
  ClientOnboardingHouseholdCard,
} from '@/modules/clientOnboarding/ClientOnboardingDeliverableCards';
import { ClientOnboardingDeliverableModalBody } from '@/modules/clientOnboarding/ClientOnboardingDeliverableModal/ClientOnboardingDeliverableModalBody';
import { useClientOnboardingDeliverableModalSidebarContent } from '@/modules/clientOnboarding/ClientOnboardingDeliverableModal/hooks/useClientOnboardingDeliverableModalSidebarContent';
import {
  DELIVERABLE_CARD_ORDER,
  DELIVERABLE_KEY_TO_VARIANT,
} from '@/modules/clientOnboarding/constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { TenantProductTier } from '@/types/schema';

import {
  ClientOnboardingDeliverableStep,
  ClientOnboardingDeliverableVariant,
} from '../types';
import { ONBOARDING_SEARCH_PARAM } from './ClientOnboardingDeliverableSelectionModal.constants';

interface CreateClientDeliverableModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialVariant: ClientOnboardingDeliverableVariant | null;
}

interface LoadingCardSkeletonProps {
  loading: boolean;
  content: ReactElement;
}

// This is a loading wrapper for the cards. One card relies on data that's loaded from the backend,
// so we show a loading skeleton for all the cards if/when the data is loading so if that card
// doesn't end up being rendered, there's not a "jumping" effect.
export function LoadingCardSkeleton({
  loading,
  content,
}: LoadingCardSkeletonProps) {
  return (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <Skeleton
          component="div"
          width="100%"
          height={238} // keep skeleton height matching card visuals
          variant="rectangular"
        />
      ) : (
        content
      )}
    </Box>
  );
}

export function ClientOnboardingDeliverableSelectionModal({
  isOpen,
  onClose,
  initialVariant,
}: CreateClientDeliverableModalProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const trackUserEvent = useTrackUserEvent();

  const [childPhase, setChildPhase] =
    useState<ClientOnboardingDeliverableStep | null>(null);

  const [selectedVariant, setSelectedVariant] =
    useState<ClientOnboardingDeliverableVariant | null>(initialVariant);

  useEffect(() => {
    setSelectedVariant(initialVariant);
  }, [initialVariant]);

  const deliverableModalSidebarContent =
    useClientOnboardingDeliverableModalSidebarContent({
      variant: selectedVariant,
    });

  const { tier } = useTenantDetailsContext();

  const { householdId, loading: fetchingHouseholdDetails } =
    useHouseholdDetailsContext();
  const householdLoaded = !!householdId && !fetchingHouseholdDetails;

  // When the modal is opened, clear the search param so that if the user leaves the page
  // and comes back, it doesn't retrigger.
  //
  // Write the URL only while the param is present. Router updates render in a
  // transition, so this effect can run with a stale `searchParams` after a card
  // has already navigated away. A stale write then resolves against the new
  // route and replaces its params, for example the Link data trowser's
  // `modalOpen` and `section`.
  const hasOnboardingParam = searchParams.has(ONBOARDING_SEARCH_PARAM);
  useEffect(() => {
    if (!isOpen || !hasOnboardingParam) return;
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(ONBOARDING_SEARCH_PARAM);
    setSearchParams(nextSearchParams, { replace: true });
  }, [isOpen, hasOnboardingParam, searchParams, setSearchParams]);

  const { aboveTitleContent, title, description, sidebarContent, mainContent } =
    useMemo(() => {
      if (selectedVariant && deliverableModalSidebarContent) {
        // Variant has been selected; render the modal body.
        return {
          ...deliverableModalSidebarContent,
          aboveTitleContent: (
            <LinkButton
              display="See all deliverables"
              onClick={() => {
                setSelectedVariant(null);
                setChildPhase(null);
              }}
              underline={false}
            />
          ),
          mainContent: (
            <ClientOnboardingDeliverableModalBody
              variant={selectedVariant}
              onClose={onClose}
              onChildPhaseChange={setChildPhase}
            />
          ),
        };
      }

      // No variant selected; render the selection cards.

      const documentSummaryCard = (
        <LoadingCardSkeleton
          loading={!householdLoaded}
          content={
            <ClientOnboardingDocumentSummaryCard
              sizeVariant="large"
              maxLines={2}
              onClick={() => {
                trackUserEvent('client_onboarding card clicked', {
                  source: 'modal',
                  cardType:
                    ClientOnboardingDeliverableVariant.SUMMARIZE_DOCUMENT,
                });
                setSelectedVariant(
                  ClientOnboardingDeliverableVariant.SUMMARIZE_DOCUMENT
                );
              }}
            />
          }
        />
      );

      const entityOverviewCard = (
        <LoadingCardSkeleton
          loading={!householdLoaded}
          content={
            <ClientOnboardingEntityCard
              maxLines={2}
              onClick={() => {
                trackUserEvent('client_onboarding card clicked', {
                  source: 'modal',
                  cardType:
                    ClientOnboardingDeliverableVariant.CREATE_ENTITY_OVERVIEW,
                });
                setSelectedVariant(
                  ClientOnboardingDeliverableVariant.CREATE_ENTITY_OVERVIEW
                );
              }}
            />
          }
        />
      );

      const strategyCard = (
        <LoadingCardSkeleton
          loading={!householdLoaded}
          content={
            <ClientOnboardingAnalysisCard
              sizeVariant="large"
              maxLines={2}
              onClick={() => {
                trackUserEvent('client_onboarding card clicked', {
                  source: 'modal',
                  cardType: ClientOnboardingDeliverableVariant.CREATE_ANALYSIS,
                });
                setSelectedVariant(
                  ClientOnboardingDeliverableVariant.CREATE_ANALYSIS
                );
              }}
            />
          }
        />
      );

      const estateSnapshotCard = (
        <LoadingCardSkeleton
          loading={!householdLoaded}
          content={
            <ClientOnboardingEstateSnapshotCard
              maxLines={2}
              onClick={() => {
                trackUserEvent('client_onboarding card clicked', {
                  source: 'modal',
                  cardType:
                    ClientOnboardingDeliverableVariant.GENERATE_ESTATE_SNAPSHOT,
                });
                setSelectedVariant(
                  ClientOnboardingDeliverableVariant.GENERATE_ESTATE_SNAPSHOT
                );
              }}
            />
          }
        />
      );

      const balanceSheetCard = (
        <LoadingCardSkeleton
          loading={!householdLoaded}
          content={
            <ClientOnboardingBalanceSheetCard
              maxLines={2}
              onClick={() => {
                trackUserEvent('client_onboarding card clicked', {
                  source: 'modal',
                  cardType:
                    ClientOnboardingDeliverableVariant.CREATE_BALANCE_SHEET,
                });
                setSelectedVariant(
                  ClientOnboardingDeliverableVariant.CREATE_BALANCE_SHEET
                );
              }}
            />
          }
        />
      );

      const householdCard = (
        <LoadingCardSkeleton
          loading={!householdLoaded}
          content={
            <ClientOnboardingHouseholdCard
              onClick={() => {
                trackUserEvent('client_onboarding card clicked', {
                  source: 'modal',
                  cardType:
                    ClientOnboardingDeliverableVariant.BUILD_OUT_HOUSEHOLD,
                });
                setSelectedVariant(
                  ClientOnboardingDeliverableVariant.BUILD_OUT_HOUSEHOLD
                );
              }}
              maxLines={2}
            />
          }
        />
      );

      const cards: ReactElement[] = [];

      const normalizedOrder = DELIVERABLE_CARD_ORDER.filter((key) => {
        if (tier === TenantProductTier.Go && key === 'BUILD_OUT_HOUSEHOLD') {
          return false;
        }

        return true;
      }).map((key) => DELIVERABLE_KEY_TO_VARIANT[key]);

      normalizedOrder.forEach((variant) => {
        switch (variant) {
          case ClientOnboardingDeliverableVariant.BUILD_OUT_HOUSEHOLD:
            cards.push(householdCard);
            break;
          case ClientOnboardingDeliverableVariant.CREATE_BALANCE_SHEET:
            cards.push(balanceSheetCard);
            break;
          case ClientOnboardingDeliverableVariant.SUMMARIZE_DOCUMENT:
            cards.push(documentSummaryCard);
            break;
          case ClientOnboardingDeliverableVariant.CREATE_ENTITY_OVERVIEW:
            cards.push(entityOverviewCard);
            break;
          case ClientOnboardingDeliverableVariant.CREATE_ANALYSIS:
            cards.push(strategyCard);
            break;
          case ClientOnboardingDeliverableVariant.GENERATE_ESTATE_SNAPSHOT:
            cards.push(estateSnapshotCard);
            break;
        }
      });

      const rows = (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {cards.map((card, idx) => (
            <Box key={idx}>{card}</Box>
          ))}
        </Box>
      );

      return {
        aboveTitleContent: initialVariant ? null : (
          <StepTracker
            connector={null}
            segments={[
              { display: 'Create client', status: StepTrackerStatus.Complete },
              { display: 'Deliverables', status: StepTrackerStatus.Active },
            ]}
            activeSegmentIndex={1}
          />
        ),
        title: <OnboardingTitle>Select your deliverable</OnboardingTitle>,
        description: (
          <Typography variant="h2light">
            Create individual client deliverables or build out the entire
            household profile
          </Typography>
        ),
        sidebarContent: null,
        mainContent: (
          <Stack spacing={2} p={6}>
            {rows}
          </Stack>
        ),
      };
    }, [
      deliverableModalSidebarContent,
      householdLoaded,
      initialVariant,
      onClose,
      selectedVariant,
      tier,
      trackUserEvent,
    ]);

  return (
    <OnboardingModal
      isOpen={isOpen}
      onClose={onClose}
      aboveTitleContent={
        childPhase === ClientOnboardingDeliverableStep.UPLOAD || !childPhase
          ? aboveTitleContent
          : null
      }
      title={title}
      description={description}
      sidebarContent={sidebarContent}
      mainContent={mainContent}
      sidebarFooterContent={
        <OnboardingSidebarButton onClick={onClose}>
          Exit
        </OnboardingSidebarButton>
      }
    />
  );
}
